import { Suspense, useCallback, useMemo } from 'react';

import {
  BrowserRouter,
  Navigate,
  Route,
  Routes as Routing,
} from 'react-router-dom';

import {
  createGlobalStyle,
  DefaultTheme,
  ThemeProvider,
} from 'styled-components';

import AppLayout from 'components/AppLayout';
import Login from 'pages/Login';
import { getThemeColors } from 'constants/Colors';
import Routes from 'constants/Routes';
import themeSizes from 'constants/Sizes';
import { AssignmentsFiltersProvider } from 'contexts/assignmentsFilters/useAssignmentsFilters';
import CasesPage from 'pages/Assignments';
import { RecoilRoot } from 'recoil';
import { CaseTypeEnum } from 'api/model';
import RequireAuth from 'components/protectedRoute/RequireAuth';
import useMe from 'contexts/authentication/useMe';
import CaseModal from 'pages/Case';
import AppInitialization from 'components/AppInitialization';
import AuthAxiosProvider from 'contexts/authentication/AuthAxiosProvider';
import 'app.css';
import ExcelExportsPages from 'pages/ExcelExports';
import ConfirmAccountPage from 'pages/ConfirmAccount';
import ResetPasswordPage from 'pages/ResetPassword';
import Forgot from 'pages/Forgot';
import CompaniesPage from 'pages/Companies';
import GlobalSearchPage from 'pages/GlobalSearch';
import InstallAppPage from 'pages/InstallApp';
import ReconfirmEmailPage from 'pages/ReconfirmEmail';
import RideUpdatesPage from 'pages/CasesWithRideUpdates';
import RideWaitingListPage from 'pages/RideWaitingList';
import ShareboxHubOverviewPage from 'pages/Sharebox';
import TestMessageGeneratorPage from 'pages/Tests/TestMessageGenerator';
import PendingAssignmentsPage from 'pages/PendingAssignments';
import ImportantCasesPage from 'pages/ImportantCases';
import ShouldGoOnTruckCasesPage from 'pages/ShouldGoOnTruckCases';
import ExportHistory from 'pages/ExcelExports/ExportHistory';
import { GoogleMapsProvider } from 'components/map/GoogleMapsContext';
import MapPage from 'pages/Map';
import PrivacyPolicyPage from 'pages/Privacy/PrivacyPolicy';
import EmployeesPage from 'pages/Employees';
import EmployeeOverview from 'pages/Employees/EmployeeOverview';
import ZipCodeAreas from 'pages/Settings/ZipCodeAreas';
import SettingsPage from 'pages/Settings';
import BusinessUnits from 'pages/Settings/BusinessUnits';
import HolidaysPage from 'pages/Settings/Holidays';
import Availability from 'pages/Employees/Availability';
import StandardizedCommentsSettingsPage from 'pages/Settings/StandardizedComments';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import TeamsOverview from 'pages/Employees/Teams';
import CaseAssignmentRequests from 'pages/CaseAssignmentRequests';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import GlobalSettingsPage from 'pages/Settings/GlobalSettings';
import VehicleClassificationPage from 'pages/Settings/VehicleClassification';
import DriverSwapPointsPage from 'pages/Settings/DriverSwapPoints';
import EstimateDurationsPage from 'pages/Settings/EstimateDurations';
import RoutePlanFeedbackSamplePage from 'pages/Settings/RoutePlanFeedbackSample';
import HomeAnchorMappingPage from 'pages/Settings/HomeAnchorMapping';
import TruckShareTargetPage from 'pages/Settings/TruckShareTarget';
import RoutePlanningSettingsPage from 'pages/Settings/RoutePlanning';
import RequireRoutePlanningSettingsAccess from 'pages/Settings/RoutePlanning/RequireRoutePlanningSettingsAccess';
import MissingPositionsPage from 'pages/Settings/RoutePlanning/MissingPositions';
import RoutePlanningGlobalSettingsPage from 'pages/Settings/RoutePlanning/RoutePlanningGlobalSettings';
import AbsenceRequests from 'pages/Employees/AbsenceRequests';
import { IdleProvider } from 'contexts/IdleContext/useIdle';
import DrivingPolicyOverview from 'pages/Employees/DrivingPolicy';
import TimeReports from 'pages/Employees/TimeReports';
import InternalDeliveryGroup from 'pages/InternalDeliveryGroup';
import InternalDeliveryAssignments from 'pages/InternalDeliveryGroup/Assignments';
import InternalDeliveryRequests from 'pages/InternalDeliveryGroup/Requests';
import InternalDeliveryOverview from 'pages/InternalDeliveryGroup/Overview';
import InternalDeliveryGroupSettings from 'pages/InternalDeliveryGroup/Budget';
import AssignmentRequestPage from 'pages/InternalDeliveryGroup/Assignments/components/AssignmentRequestPage';
import DrivingPolicyWarningsPage from 'pages/Employees/DrivingPolicyWarnings';
import { AssignmentRequestsFiltersProvider } from 'pages/InternalDeliveryGroup/Requests/components/useAssignmentRequestFilters';
import ExcelExports from 'pages/ExcelExports/ExcelExports';
import ReceiptsList from 'pages/ExcelExports/ReceiptsList';
import ProductExpenseTypeMappingsPage from 'pages/Settings/ProductExpenseTypeMappings';
import PaymentCards from 'pages/Employees/PaymentCards';
import { ApiExceptionResponse } from 'api/types';
import MobileAvail from 'pages/Employees/Availability/MobileAvail';
import { showException } from 'utils/exception-helper';
import CompanyDepartmentsSettingsPage from 'pages/Settings/CompanyDepartments';
import EkonomiPage from 'pages/Ekonomi';
import PriceListsPage from 'pages/Ekonomi/PriceLists';
import GoSupportPage from 'pages/GoSupport';
import TransportLogisticsPage from 'pages/TransportLogistics';
import FollowUpPage from 'pages/TransportLogistics/FollowUpPage';
import TestAIPromptPage from 'pages/Tests/TestAIPrompt';
import Roles, {
  AvailabilityViewAccessPolicy,
  BulkSmsAccessPolicy,
  DefaultWebAdminPolicy,
  EmployeesPageAccessPolicy,
  HomeAnchorMappingAccessPolicy,
  HRSpeedingAccessPolicy,
  InternalDeliveryGroupAccessPolicy,
  isAvailabilityReadOnly,
  isStartklarOnly,
  KPIsAccessPolicy,
  LasDaysAccessPolicy,
  ManagementReportAccessPolicy,
  MapsApiKeyTestAccessPolicy,
  QualityFlagsAccessPolicy,
  RiddermarkKPIsAccessPolicy,
  RoutePlanFeedbackTriggerAccessPolicy,
  RoutePlanningGlobalSettingsAccessPolicy,
  StartklarViewAccessPolicy,
  TruckShareTargetViewAccessPolicy,
  WorkTimeReportAccessPolicy,
} from 'constants/Roles';
import TestDrivesPage from 'pages/InternalDeliveryGroup/TestDrives';
import AfterControlPage from 'pages/AfterControl';
import AutoApproveAnalyticsPage from './pages/AfterControl/AutoApproveAnalytics';
import QualityPage from 'pages/Quality';
import DeclinedAssignmentsPage from 'pages/Quality/DeclinedAssignments';
import BulkSmsPage from 'pages/Quality/BulkSms';
import HRPage from 'pages/HR';
import GreenPlatePage from 'pages/HR/GreenPlates/GreenPlatePage';
import LasDaysPage from 'pages/HR/LasDays/LasDaysPage';
import WorkTimeReportPage from 'pages/HR/WorkTimeReport/WorkTimeReportPage';
import HRSpeedingPage from 'pages/HR/Speeding/HRSpeedingPage';
import TestAIInferencePage from 'pages/Tests/TestAIInference';
import TestMapsApiKeyPage from 'pages/Tests/TestMapsApiKey';
import UnclosedCasesPage from 'pages/UnclosedCases';
import KPIsPage from 'pages/KPIs';
import RiddermarkKPIsPage from 'pages/KPIs/RiddermarkKPIs';
import ManagementReportPage from 'pages/ManagementReport';
import RequireRiddermarkReportingAccess from 'pages/KPIs/RequireRiddermarkReportingAccess';
import DayPlanningPage from 'pages/DayPlanning';
import RuttplaneringPage from 'pages/TransportLogistics/Ruttplanering';
import RoutePlanFeedbackPage from 'pages/TransportLogistics/Ruttfeedback';
import StartklarPage from 'pages/Startklar';

const GlobalStyle = createGlobalStyle`
  html, body {
    font-size: ${({ theme }) => theme.sizes.font.small};
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html, body, #root {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
`;

const LandingRedirect: React.FC = () => {
  const me = useMe();
  let to: string = Routes.purchases.index;
  if (isStartklarOnly(me?.roles)) {
    to = Routes.startklar.index;
  } else if (isAvailabilityReadOnly(me?.roles)) {
    to = Routes.employees.availability;
  }
  return <Navigate replace to={to} />;
};

const App: React.FC = () => {
  const theme = useMemo(
    (): DefaultTheme => ({
      colors: getThemeColors(),
      sizes: themeSizes,
    }),
    []
  );

  const makeCasesRoutes = (
    caseType: CaseTypeEnum,
    casesPagePath: string,
    caseModalPath: string
  ) => (
    <Route
      element={
        <RequireAuth>
          <CasesPage casePathPattern={caseModalPath} caseType={caseType} />
        </RequireAuth>
      }
      path={casesPagePath}
    >
      <Route
        element={
          <RequireAuth>
            <CaseModal navigatesBackTo={casesPagePath} />
          </RequireAuth>
        }
        path={caseModalPath}
      />
    </Route>
  );

  const handleError = useCallback((error: any) => {
    // error.response saknas vid nätverksfel — en oguardad deref här kastade och
    // det yttre try/catch:et svalde då felet helt (ingen toast). Misslyckade
    // mutationer (t.ex. borttag av uppdrag) såg därför ut att lyckas.
    const status = (error?.response?.data as ApiExceptionResponse | undefined)
      ?.status;
    if (status === 401) {
      return;
    }
    showException(error);
  }, []);

  const queryClient = useMemo(
    () =>
      new QueryClient({
        // defaultOptions: {
        //   queries: {
        //     refetchOnWindowFocus: false,
        //     retry: false,
        //   },
        // },
        queryCache: new QueryCache({
          onError: handleError,
        }),
        mutationCache: new MutationCache({
          onError: handleError,
        }),
      }),
    // handleError has an empty dep array and is stable for the lifetime of the component
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <IdleProvider milliseconds={60000}>
      <QueryClientProvider client={queryClient}>
        <RecoilRoot>
          <ThemeProvider theme={theme}>
            <AuthAxiosProvider>
              <GlobalStyle />
              <AppInitialization />
              <BrowserRouter>
                <AssignmentsFiltersProvider>
                  <AssignmentRequestsFiltersProvider>
                    <GoogleMapsProvider>
                      <Suspense fallback="Laddar!!!">
                        <Routing>
                          <Route element={<AppLayout />} path={Routes.index}>
                            {/* automatically navigate to Routes.cases.index if user lands on Routes.index */}
                            <Route
                              element={
                                <RequireAuth>
                                  <LandingRedirect />
                                </RequireAuth>
                              }
                              path={Routes.index}
                            />

                            <Route
                              element={<Login />}
                              path={Routes.account.login}
                            />
                            <Route
                              element={<ConfirmAccountPage />}
                              path={Routes.account.confirm}
                            />
                            <Route
                              element={<ReconfirmEmailPage />}
                              path={Routes.account.reconfirm}
                            />
                            {makeCasesRoutes(
                              CaseTypeEnum.Purchase,
                              Routes.purchases.index,
                              Routes.purchases.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Auction,
                              Routes.auction.index,
                              Routes.auction.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.RentedStaff,
                              Routes.rentedStaff.index,
                              Routes.rentedStaff.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Samakning,
                              Routes.samakning.index,
                              Routes.samakning.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.HomeDelivery,
                              Routes.homeDeliveries.index,
                              Routes.homeDeliveries.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.InternalDelivery,
                              Routes.internalDeliveries.index,
                              Routes.internalDeliveries.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.FaciliyExchange,
                              Routes.facilityExchange.index,
                              Routes.facilityExchange.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.InternalDeliverySales,
                              Routes.internalDeliverySales.index,
                              Routes.internalDeliverySales.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Verkstadsbilar,
                              Routes.verkstadsbilar.index,
                              Routes.verkstadsbilar.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.FacilityToFacility,
                              Routes.facilityToFacility.index,
                              Routes.facilityToFacility.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.FacilityTransfer,
                              Routes.facilityTransfer.index,
                              Routes.facilityTransfer.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.RMOther,
                              Routes.rmOther.index,
                              Routes.rmOther.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Avyttringar,
                              Routes.avyttringar.index,
                              Routes.avyttringar.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Garantiarenden,
                              Routes.garantiarenden.index,
                              Routes.garantiarenden.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.AterTillLogistik,
                              Routes.aterTillLogistik.index,
                              Routes.aterTillLogistik.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Dackkorning,
                              Routes.dackkorning.index,
                              Routes.dackkorning.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Swamp,
                              Routes.swamp.index,
                              Routes.swamp.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.TestDrive,
                              Routes.testDrives.index,
                              Routes.testDrives.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.Placeholder,
                              Routes.placeholder.index,
                              Routes.placeholder.case
                            )}
                            {makeCasesRoutes(
                              CaseTypeEnum.CarTransportTruck,
                              Routes.carTransportTrucks.index,
                              Routes.carTransportTrucks.case
                            )}

                            {/* Mellanlandningar */}
                            <Route
                              element={
                                <RequireAuth>
                                  <PendingAssignmentsPage />
                                </RequireAuth>
                              }
                              path={Routes.pendingAssignments.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={
                                        Routes.pendingAssignments.index
                                      }
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.pendingAssignments.case}
                              />
                            </Route>
                            <Route
                              element={
                                <RequireAuth>
                                  <ImportantCasesPage />
                                </RequireAuth>
                              }
                              path={Routes.viktigt.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={Routes.viktigt.index}
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.viktigt.case}
                              />
                            </Route>
                            <Route
                              element={
                                <RequireAuth>
                                  <ShouldGoOnTruckCasesPage />
                                </RequireAuth>
                              }
                              path={Routes.skaPaLastbil.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={
                                        Routes.skaPaLastbil.index
                                      }
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.skaPaLastbil.case}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <TransportLogisticsPage />
                                </RequireAuth>
                              }
                              path={Routes.transportLogistics.index}
                            >
                              <Route
                                index
                                element={
                                  <Navigate
                                    to={Routes.transportLogistics.followUp}
                                    replace
                                  />
                                }
                              />
                              <Route
                                element={<DayPlanningPage />}
                                path={Routes.transportLogistics.dayPlanning}
                              />
                              <Route
                                element={<FollowUpPage />}
                                path={Routes.transportLogistics.followUp}
                              />
                              <Route
                                element={<RuttplaneringPage />}
                                path={Routes.transportLogistics.routePlanning}
                              />
                              <Route
                                element={<RoutePlanFeedbackPage />}
                                path={
                                  Routes.transportLogistics.routePlanFeedback
                                }
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={StartklarViewAccessPolicy}>
                                  <StartklarPage />
                                </RequireAuth>
                              }
                              path={Routes.startklar.index}
                            />

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <GoSupportPage />
                                </RequireAuth>
                              }
                              path={Routes.goSupport.index}
                            />

                            <Route
                              element={
                                <RequireAuth
                                  policy={{
                                    allow: [
                                      Roles.Efterkontroll,
                                      Roles.SuperAdmin,
                                    ],
                                    deny: DefaultWebAdminPolicy.deny,
                                  }}
                                >
                                  <AfterControlPage />
                                </RequireAuth>
                              }
                              path={Routes.afterControl.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={
                                        Routes.afterControl.index
                                      }
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.afterControl.autoApproval.case}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={HRSpeedingAccessPolicy}>
                                    <HRSpeedingPage />
                                  </RequireAuth>
                                }
                                path={Routes.afterControl.speeding}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth
                                  policy={{
                                    allow: [Roles.SuperAdmin],
                                    deny: DefaultWebAdminPolicy.deny,
                                  }}
                                >
                                  <AutoApproveAnalyticsPage />
                                </RequireAuth>
                              }
                              path={Routes.autoApproveAnalytics.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={
                                        Routes.autoApproveAnalytics.index
                                      }
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.autoApproveAnalytics.case}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={QualityFlagsAccessPolicy}>
                                  <QualityPage />
                                </RequireAuth>
                              }
                              path={Routes.quality.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={Routes.quality.index}
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.quality.case}
                              />
                              <Route
                                element={
                                  <RequireAuth
                                    policy={QualityFlagsAccessPolicy}
                                  >
                                    <DeclinedAssignmentsPage />
                                  </RequireAuth>
                                }
                                path={Routes.quality.declined}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={BulkSmsAccessPolicy}>
                                    <BulkSmsPage />
                                  </RequireAuth>
                                }
                                path={Routes.quality.bulkSms}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={HRSpeedingAccessPolicy}>
                                    <HRSpeedingPage />
                                  </RequireAuth>
                                }
                                path={Routes.quality.speeding}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth
                                  policy={{
                                    allow: [
                                      Roles.HR,
                                      Roles.SuperAdmin,
                                      Roles.KvalitetOUtbildning,
                                    ],
                                    deny: DefaultWebAdminPolicy.deny,
                                  }}
                                >
                                  <HRPage />
                                </RequireAuth>
                              }
                              path={Routes.hr.index}
                            >
                              {/* Redirect from HR index to greenplates since it is the first and only page for now */}
                              <Route
                                index
                                element={
                                  <RequireAuth
                                    policy={{
                                      allow: [
                                        Roles.HR,
                                        Roles.SuperAdmin,
                                        Roles.KvalitetOUtbildning,
                                      ],
                                      deny: DefaultWebAdminPolicy.deny,
                                    }}
                                  >
                                    <Navigate
                                      replace
                                      to={Routes.hr.greenplates}
                                    />
                                  </RequireAuth>
                                }
                              />
                              <Route
                                element={
                                  <RequireAuth
                                    policy={{
                                      allow: [
                                        Roles.HR,
                                        Roles.SuperAdmin,
                                        Roles.KvalitetOUtbildning,
                                      ],
                                      deny: DefaultWebAdminPolicy.deny,
                                    }}
                                  >
                                    <GreenPlatePage />
                                  </RequireAuth>
                                }
                                path={Routes.hr.greenplates}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={LasDaysAccessPolicy}>
                                    <LasDaysPage />
                                  </RequireAuth>
                                }
                                path={Routes.hr.lasDays}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={WorkTimeReportAccessPolicy}>
                                    <WorkTimeReportPage />
                                  </RequireAuth>
                                }
                                path={Routes.hr.workTimeReport}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <RideUpdatesPage />
                                </RequireAuth>
                              }
                              path={Routes.rideUpdates.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={Routes.rideUpdates.index}
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.rideUpdates.case}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <RideWaitingListPage />
                                </RequireAuth>
                              }
                              path={Routes.rideWaitingList.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={
                                        Routes.rideWaitingList.index
                                      }
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.rideWaitingList.case}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <ShareboxHubOverviewPage />
                                </RequireAuth>
                              }
                              path={Routes.sharebox.index}
                            />

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <ExcelExportsPages />
                                </RequireAuth>
                              }
                              path={Routes.excelExports.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <ExcelExports />
                                  </RequireAuth>
                                }
                                path={Routes.excelExports.exports}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <ReceiptsList />
                                  </RequireAuth>
                                }
                                path={Routes.excelExports.receipts}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <ExportHistory />
                                  </RequireAuth>
                                }
                                path={Routes.excelExports.history}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <UnclosedCasesPage />
                                  </RequireAuth>
                                }
                                path={Routes.excelExports.unclosedCases}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <CompaniesPage />
                                </RequireAuth>
                              }
                              path={Routes.companies.index}
                            />
                            <Route
                              element={
                                <RequireAuth>
                                  <GlobalSearchPage
                                    casePathPattern={Routes.search.case}
                                  />
                                </RequireAuth>
                              }
                              path={Routes.search.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <CaseModal
                                      navigatesBackTo={Routes.search.index}
                                    />
                                  </RequireAuth>
                                }
                                path={Routes.search.case}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth>
                                  <CaseModal
                                    navigatesBackTo={Routes.search.index}
                                  />
                                </RequireAuth>
                              }
                              path={Routes.case.case}
                            />

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <MapPage />
                                </RequireAuth>
                              }
                              path={Routes.map.index}
                            />

                            <Route
                              element={
                                <RequireAuth policy={EmployeesPageAccessPolicy}>
                                  <EmployeesPage />
                                </RequireAuth>
                              }
                              path={Routes.employees.index}
                            >
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <MobileAvail />
                                  </RequireAuth>
                                }
                                path={Routes.employees.mobile}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <EmployeeOverview />
                                  </RequireAuth>
                                }
                                path={Routes.employees.index}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <TimeReports />
                                  </RequireAuth>
                                }
                                path={Routes.employees.timeReports}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <TeamsOverview />
                                  </RequireAuth>
                                }
                                path={Routes.employees.teams}
                              />
                              <Route
                                element={
                                  <RequireAuth
                                    policy={AvailabilityViewAccessPolicy}
                                  >
                                    <Availability />
                                  </RequireAuth>
                                }
                                path={Routes.employees.availability}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <AbsenceRequests />
                                  </RequireAuth>
                                }
                                path={Routes.employees.absenceRequests}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <DrivingPolicyWarningsPage />
                                  </RequireAuth>
                                }
                                path={Routes.employees.drivingPolicyWarnings}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <DrivingPolicyOverview />
                                  </RequireAuth>
                                }
                                path={Routes.employees.drivingPolicy}
                              />
                              <Route
                                element={
                                  <RequireAuth policy={DefaultWebAdminPolicy}>
                                    <PaymentCards />
                                  </RequireAuth>
                                }
                                path={Routes.employees.paymentCards}
                              />
                            </Route>

                            <Route
                              element={<Forgot />}
                              path={Routes.forgot.index}
                            />
                            <Route
                              element={<ResetPasswordPage />}
                              path={Routes.resetpassword.index}
                            />

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <TestMessageGeneratorPage />
                                </RequireAuth>
                              }
                              path={Routes.tests.testMessageGenerator}
                            />
                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <TestAIPromptPage />
                                </RequireAuth>
                              }
                              path={Routes.tests.testAIPrompt}
                            />
                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <TestAIInferencePage />
                                </RequireAuth>
                              }
                              path={Routes.tests.testAIInference}
                            />
                            <Route
                              element={
                                <RequireAuth
                                  policy={MapsApiKeyTestAccessPolicy}
                                >
                                  <TestMapsApiKeyPage />
                                </RequireAuth>
                              }
                              path={Routes.tests.testMapsApiKey}
                            />

                            <Route
                              element={<InstallAppPage />}
                              path={Routes.appInstall.index}
                            />

                            <Route
                              element={<PrivacyPolicyPage />}
                              path={Routes.privacy.index}
                            />

                            <Route
                              element={
                                <RequireAuth policy={KPIsAccessPolicy}>
                                  <KPIsPage />
                                </RequireAuth>
                              }
                              path={Routes.kpis.index}
                            />

                            <Route
                              element={
                                <RequireAuth
                                  policy={RiddermarkKPIsAccessPolicy}
                                >
                                  <RiddermarkKPIsPage />
                                </RequireAuth>
                              }
                              path={Routes.kpis.riddermark}
                            />

                            <Route
                              element={
                                <RequireAuth
                                  policy={ManagementReportAccessPolicy}
                                >
                                  <RequireRiddermarkReportingAccess>
                                    <ManagementReportPage />
                                  </RequireRiddermarkReportingAccess>
                                </RequireAuth>
                              }
                              path={Routes.kpis.managementReport}
                            />

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <SettingsPage />
                                </RequireAuth>
                              }
                              path={Routes.settings.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <ZipCodeAreas />
                                  </RequireAuth>
                                }
                                path={Routes.settings.zipCodeAreas}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <BusinessUnits />
                                  </RequireAuth>
                                }
                                path={Routes.settings.businessUnits}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <HolidaysPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.holidays}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <GlobalSettingsPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.globalSettings}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <DriverSwapPointsPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.driverSwapPoints}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <VehicleClassificationPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.vehicleClassification}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <EstimateDurationsPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.estimateDurations}
                              />
                              <Route
                                element={
                                  <RequireAuth
                                    policy={TruckShareTargetViewAccessPolicy}
                                  >
                                    <TruckShareTargetPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.truckShareTarget}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <RequireRoutePlanningSettingsAccess>
                                      <RoutePlanningSettingsPage />
                                    </RequireRoutePlanningSettingsAccess>
                                  </RequireAuth>
                                }
                                path={Routes.settings.routePlanning.index}
                              >
                                <Route
                                  element={
                                    <Navigate
                                      replace
                                      to={
                                        Routes.settings.routePlanning.positions
                                      }
                                    />
                                  }
                                  index
                                />
                                <Route
                                  element={<MissingPositionsPage />}
                                  path={Routes.settings.routePlanning.positions}
                                />
                                <Route
                                  element={
                                    <RequireAuth
                                      policy={HomeAnchorMappingAccessPolicy}
                                    >
                                      <HomeAnchorMappingPage />
                                    </RequireAuth>
                                  }
                                  path={
                                    Routes.settings.routePlanning
                                      .homeAnchorMapping
                                  }
                                />
                                <Route
                                  element={
                                    <RequireAuth
                                      policy={
                                        RoutePlanFeedbackTriggerAccessPolicy
                                      }
                                    >
                                      <RoutePlanFeedbackSamplePage />
                                    </RequireAuth>
                                  }
                                  path={
                                    Routes.settings.routePlanning.feedbackSample
                                  }
                                />
                                <Route
                                  element={
                                    <RequireAuth
                                      policy={
                                        RoutePlanningGlobalSettingsAccessPolicy
                                      }
                                    >
                                      <RoutePlanningGlobalSettingsPage />
                                    </RequireAuth>
                                  }
                                  path={
                                    Routes.settings.routePlanning.globalSettings
                                  }
                                />
                              </Route>
                              <Route
                                element={
                                  <Navigate
                                    replace
                                    to={
                                      Routes.settings.routePlanning
                                        .homeAnchorMapping
                                    }
                                  />
                                }
                                path={Routes.settings.legacyHomeAnchorMapping}
                              />
                              <Route
                                element={
                                  <Navigate
                                    replace
                                    to={
                                      Routes.settings.routePlanning
                                        .feedbackSample
                                    }
                                  />
                                }
                                path={
                                  Routes.settings.legacyRoutePlanFeedbackSample
                                }
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <ProductExpenseTypeMappingsPage />
                                  </RequireAuth>
                                }
                                path={
                                  Routes.settings.productExpenseTypeMappings
                                }
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <HolidaysPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.index}
                              />
                            </Route>
                            <Route
                              element={<PrivacyPolicyPage />}
                              path={Routes.privacy.index}
                            />

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <SettingsPage />
                                </RequireAuth>
                              }
                              path={Routes.settings.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <ZipCodeAreas />
                                  </RequireAuth>
                                }
                                path={Routes.settings.zipCodeAreas}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <BusinessUnits />
                                  </RequireAuth>
                                }
                                path={Routes.settings.businessUnits}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <HolidaysPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.holidays}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <HolidaysPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.index}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <Suspense fallback={<LoadingSpinner />}>
                                      <StandardizedCommentsSettingsPage />
                                    </Suspense>
                                  </RequireAuth>
                                }
                                path={Routes.settings.standardizedComments}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <Suspense fallback={<LoadingSpinner />}>
                                      <CompanyDepartmentsSettingsPage />
                                    </Suspense>
                                  </RequireAuth>
                                }
                                path={Routes.settings.companyDepartments}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <VehicleClassificationPage />
                                  </RequireAuth>
                                }
                                path={Routes.settings.vehicleClassification}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth policy={DefaultWebAdminPolicy}>
                                  <CaseAssignmentRequests />
                                </RequireAuth>
                              }
                              path={Routes.caseAssignmentRequests.index}
                            />

                            <Route
                              element={
                                <RequireAuth
                                  policy={InternalDeliveryGroupAccessPolicy}
                                >
                                  <InternalDeliveryGroup />
                                </RequireAuth>
                              }
                              // Annons-v2 Uppdrags-översikt
                              path={Routes.internalDeliveryGroup.index}
                            >
                              <Route
                                element={
                                  <RequireAuth>
                                    <InternalDeliveryAssignments />
                                  </RequireAuth>
                                }
                                path={Routes.internalDeliveryGroup.index}
                              />
                              {makeCasesRoutes(
                                CaseTypeEnum.RentedStaff,
                                Routes.internalDeliveryGroup.index,
                                Routes.internalDeliveryGroup.assignments.case
                              )}
                              {makeCasesRoutes(
                                CaseTypeEnum.FaciliyExchange,
                                Routes.internalDeliveryGroup.index,
                                Routes.internalDeliveryGroup.assignments.case
                              )}
                              {makeCasesRoutes(
                                CaseTypeEnum.CarTransportTruck,
                                Routes.internalDeliveryGroup.carTransportTrucks
                                  .index,
                                Routes.internalDeliveryGroup.carTransportTrucks
                                  .case
                              )}
                              <Route
                                element={
                                  <RequireAuth>
                                    <InternalDeliveryAssignments />
                                  </RequireAuth>
                                }
                                path={Routes.internalDeliveryGroup.index}
                              />

                              <Route
                                element={
                                  <RequireAuth>
                                    <TestDrivesPage />
                                  </RequireAuth>
                                }
                                path={
                                  Routes.internalDeliveryGroup.testDrives.index
                                }
                              >
                                <Route
                                  element={
                                    <RequireAuth>
                                      <CaseModal
                                        navigatesBackTo={
                                          Routes.internalDeliveryGroup
                                            .testDrives.index
                                        }
                                      />
                                    </RequireAuth>
                                  }
                                  path={
                                    Routes.internalDeliveryGroup.testDrives.case
                                  }
                                />
                              </Route>

                              <Route
                                element={
                                  <RequireAuth>
                                    <InternalDeliveryRequests />
                                  </RequireAuth>
                                }
                                path={
                                  Routes.internalDeliveryGroup.requests.index
                                }
                              >
                                <Route
                                  element={
                                    <RequireAuth>
                                      <AssignmentRequestPage
                                        navigatesBackTo={
                                          Routes.internalDeliveryGroup.requests
                                            .index
                                        }
                                      />
                                    </RequireAuth>
                                  }
                                  path={
                                    Routes.internalDeliveryGroup.requests
                                      .request
                                  }
                                />
                              </Route>

                              <Route
                                element={
                                  <RequireAuth>
                                    <InternalDeliveryOverview />
                                  </RequireAuth>
                                }
                                path={Routes.internalDeliveryGroup.overview}
                              />
                              <Route
                                element={
                                  <RequireAuth>
                                    <InternalDeliveryGroupSettings />
                                  </RequireAuth>
                                }
                                path={Routes.internalDeliveryGroup.budget}
                              />
                            </Route>

                            <Route
                              element={
                                <RequireAuth
                                  policy={{
                                    allow: [Roles.SuperAdmin, Roles.Ekonomi],
                                    deny: DefaultWebAdminPolicy.deny,
                                  }}
                                >
                                  <EkonomiPage />
                                </RequireAuth>
                              }
                              path={Routes.ekonomi.index}
                            >
                              <Route
                                element={<PriceListsPage />}
                                path={Routes.ekonomi.priceLists}
                              />
                              <Route
                                index
                                element={
                                  <Navigate
                                    to={Routes.ekonomi.priceLists}
                                    replace
                                  />
                                }
                              />
                            </Route>

                            <Route
                              element={<h1>Sidan hittades inte</h1>}
                              path="/not-found"
                            />
                            <Route
                              element={<h1>Sidan hittades inte</h1>}
                              path="*"
                            />
                          </Route>
                        </Routing>
                      </Suspense>
                    </GoogleMapsProvider>
                  </AssignmentRequestsFiltersProvider>
                </AssignmentsFiltersProvider>
              </BrowserRouter>
            </AuthAxiosProvider>
          </ThemeProvider>
        </RecoilRoot>
      </QueryClientProvider>
    </IdleProvider>
  );
};

export default App;
